<template>
    <div class="pagination-controls">
        <label class="page-size-label">
            Page size:
            <select
                :value="pageSize"
                @change="$emit('update:pageSize', Number(($event.target as HTMLSelectElement).value))"
                class="page-size-select"
            >
                <option
                    v-for="size in pageSizeOptions"
                    :key="size"
                    :value="size"
                >
                    {{ size }}
                </option>
            </select>
        </label>
        <span class="pagination-info">
            <span class="pagination-num">{{ paginationStart }}</span>
            <span class="pagination-sep"> to </span>
            <span class="pagination-num">{{ paginationEnd }}</span>
            <span class="pagination-sep"> of </span>
            <span class="pagination-num">{{ totalItems }}</span>
        </span>
        <div class="pagination-navigation">
            <button
                @click="$emit('first-page')"
                :disabled="currentPage === 1"
                class="pagination-btn"
                title="First page"
                aria-label="First page"
            >
                <span
                    class="ag-icon ag-icon-first"
                    aria-hidden="true"
                />
            </button>
            <button
                @click="$emit('previous-page')"
                :disabled="currentPage === 1"
                class="pagination-btn"
                title="Previous page"
                aria-label="Previous page"
            >
                <span
                    class="ag-icon ag-icon-previous"
                    aria-hidden="true"
                />
            </button>
            <span class="page-indicator">
                <span class="pagination-sep"> Page </span>
                <span class="pagination-num">{{ currentPage }}</span>
                <span class="pagination-sep"> of </span>
                <span class="pagination-num">{{ totalPages }}</span>
            </span>
            <button
                @click="$emit('next-page')"
                :disabled="currentPage === totalPages"
                class="pagination-btn"
                title="Next page"
                aria-label="Next page"
            >
                <span
                    class="ag-icon ag-icon-next"
                    aria-hidden="true"
                />
            </button>
            <button
                @click="$emit('last-page')"
                :disabled="currentPage === totalPages"
                class="pagination-btn"
                title="Last page"
                aria-label="Last page"
            >
                <span
                    class="ag-icon ag-icon-last"
                    aria-hidden="true"
                />
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
defineOptions({ name: 'PaginationControls' });

interface Props {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalItems: number;
    paginationStart: number;
    paginationEnd: number;
    pageSizeOptions?: number[];
}

withDefaults(defineProps<Props>(), {
    pageSizeOptions: () => [
        5, 10, 20
    ]
});

defineEmits<{
    'update:pageSize': [value: number];
    'first-page': [];
    'previous-page': [];
    'next-page': [];
    'last-page': [];
}>();
</script>

<style scoped>
.pagination-controls {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 18px;
    padding: 0px;
    background-color: #fff;
    font-size: 13px;
    color: #495057;
}
.pagination-controls > label {
    padding-bottom: 0px;
}
.page-size-label {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    cursor: default;
    white-space: nowrap;
        padding: 0px 16px;

}

.page-size-select {
    color: #495057;
    transition: border-color 0.15s ease-in-out;
    min-width: 64px;
    margin: 5px 0px;
    width: 80px;
    appearance: none; /* Disable the default arrow */
    -webkit-appearance: none; /* For WebKit-based browsers */
    -moz-appearance: none; /* For Firefox */
    padding: 10px 35px 10px 10px;
    font-size: 13px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    background-color: #fff;
    background-image: url('data:image/svg+xml;utf8,<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 9L12 15L18 9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor"/></svg>');
    background-repeat: no-repeat;
    background-position: right 10px center;
    background-size: 20px;
    cursor: pointer;

}

.pagination-info {
    font-weight: 400;
    color: #1a2021;
    font-size: 16px;
    padding: 0px 16px;

}

.pagination-navigation {
    display: flex;
    align-items: center;
    gap: 0px;
    padding: 0px 16px;
}

.page-indicator {
    font-weight: 500;
    font-size: 16px;
}

.pagination-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 4px;
    padding: 0px;
    background-color: #fff;
    color: #495057;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.15s ease-in-out;
}

.pagination-btn:hover:not(:disabled) {
    background-color: #e9ecef;
    border-color: #adb5bd;
}

.pagination-btn:active:not(:disabled) {
    background-color: #dee2e6;
}

.pagination-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.pagination-btn span {
    display: inline-block;
    line-height: 1;
}

.pagination-num {
    font-weight: 700; /* bold for numbers */
    color: inherit;
}

/* ag-grid icons: show if ag-grid CSS is available, otherwise show fallback */
.pagination-btn .ag-icon {
    display: inline-block;
}
.pagination-btn .pagination-fallback {
    display: inline-block;
}

.pagination-btn .ag-icon + .pagination-fallback {
    margin-left: 0.25rem;
}
</style>
